/**
 * Secure URL validation utilities for RentWise
 * Prevents open redirection attacks while allowing legitimate internal URLs
 */

// Allowed URL patterns for safe redirection
const ALLOWED_URL_PATTERNS = [
  // Tenant dashboard
  /^\/dashboard\/tenant(\/.*)?$/,
  
  // Leave review process
  /^\/leave-review(\/.*)?$/,
  
  // Review pages
  /^\/listings\/reviews\/[a-z]{2}\/[a-z-]+\/[a-z-]+\/[a-z-]+\/[a-zA-Z0-9]{9}\/[a-z0-9-]+$/,
  
  // Other safe internal pages
  /^\/browse(\/.*)?$/,
  /^\/pricing(\/.*)?$/,
  /^\/faq(\/.*)?$/,
  /^\/contact(\/.*)?$/,
  /^\/about(\/.*)?$/,
] as const;

/**
 * Validates and sanitizes a return URL to prevent open redirection attacks
 * @param url - The URL to validate (from query params or user input)
 * @param fallback - Fallback URL if validation fails
 * @returns Safe URL for redirection
 */
export function validateReturnUrl(
  url: string | string[] | undefined, 
  fallback: string = '/dashboard/tenant'
): string {
  // Handle undefined, null, or array values
  if (!url || Array.isArray(url)) {
    return fallback;
  }

  try {
    // Decode and clean the URL
    const cleanUrl = decodeURIComponent(url.trim());
    
    // Must be an internal path starting with /
    if (!cleanUrl.startsWith('/')) {
      return fallback;
    }
    
    // Reject protocol-relative URLs (//example.com)
    if (cleanUrl.startsWith('//')) {
      return fallback;
    }
    
    // Reject URLs with protocols (javascript:, data:, etc.)
    if (cleanUrl.includes(':')) {
      return fallback;
    }
    
    // Reject path traversal attempts
    if (cleanUrl.includes('..') || cleanUrl.includes('%2e%2e')) {
      return fallback;
    }
    
    // Check against allowed patterns
    const isAllowed = ALLOWED_URL_PATTERNS.some(pattern => 
      pattern.test(cleanUrl)
    );
    
    if (!isAllowed) {
      console.warn(`Blocked potentially unsafe redirect URL: ${cleanUrl}`);
      return fallback;
    }
    
    return cleanUrl;
    
  } catch (error) {
    console.warn(`URL validation error for ${url}:`, error);
    return fallback;
  }
}

/**
 * Creates a return URL for plan management page with proper validation
 * @param currentUrl - The current page URL to return to after plan management
 * @returns Properly formatted plan management URL with return parameter
 */
export function createPlanManagementUrl(currentUrl: string): string {
  const baseUrl = '/dashboard/tenant/manage-plan';
  
  // Validate the return URL first
  const safeReturnUrl = validateReturnUrl(currentUrl);
  
  // Only add returnUrl param if it's different from the default fallback
  if (safeReturnUrl !== '/dashboard/tenant') {
    const encodedReturnUrl = encodeURIComponent(safeReturnUrl);
    return `${baseUrl}?returnUrl=${encodedReturnUrl}`;
  }
  
  return baseUrl;
}

/**
 * Extracts the current review page URL from window location
 * Safe for both server and client environments
 * @returns Current URL or fallback for server environments
 */
export function getCurrentReviewUrl(): string {
  if (typeof window === 'undefined') {
    // Server environment - return fallback
    return '/dashboard/tenant';
  }
  
  return window.location.pathname;
} 